import { customElement, property } from 'lit/decorators.js'
import { css, html, LitElement, TemplateResult, nothing } from 'lit'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { redirectToPath } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { consume } from '@lit/context'
import {
  getOpenQuotationContext,
  NotFetchedYet,
  openQuotationContext,
  updateQuotationInternalWithoutRequestContext
} from 'components/ordering/quotation/components/context/quotation-context.js'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import { NotFoundError } from 'components/ordering/quotation/api/dtos/error.js'
import { isQuotationDTO } from 'components/ordering/quotation/components/is-quotation-dto.js'
import { listenForQuotationUpdates } from 'components/ordering/quotation/components/quotation-update-event.js'
import { CSCQuotationRequestSlideIn } from 'components/ordering/quotation/components/request/slide-in.js'

initializeIconsByRole({
  'csc-quotation-header-button.quotation-icon': { name: 'file-invoice-dollar', style: 'light' },
})

const translations = {
  whenOpenQuotation: () => t('quotation', {
    ns: 'csc-quotation-header-button',
    default: 'Quotation'
  }),
  whenNoOpenQuotation: () => t('quotations', {
    ns: 'csc-quotation-header-button',
    default: 'Quotations'
  })
}

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

@customElement('csc-quotation-header-button')
export class CSCQuotationHeaderButton extends LitElement {
  static override styles = css`
    .as-button-wrapper {
      position: relative;
      display: inline-flex;
    }
    .as-button-wrapper cse-badge {
      position: absolute;
      top: -6px;
      right: -6px;
    }
  `
  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  @consume({ context: openQuotationContext, subscribe: true })
  private quotation!: QuotationDTO | NotFoundError | NotFetchedYet

  @consume({ context: getOpenQuotationContext })
  private getOpenQuotation!: () => Promise<void>

  @consume({ context: updateQuotationInternalWithoutRequestContext })
  private updateQuotationInternalWithoutRequest!: (quotation: QuotationDTO) => Promise<void>

  @property({ type: Boolean, attribute: 'hide-caption' })
  hideCaption = false

  @property({ type: Boolean, attribute: 'hide-icon' })
  hideIcon = false

  @property({ type: String, attribute: 'icon-size' })
  iconSize: IconSize = 'lg'

  @property({ type: Boolean, attribute: 'as-button' })
  asButton = false

  @property({ type: String })
  size: 'default' | 'small' = 'default'

  get totalProductsBadge(): TemplateResult {
    let totalLines = 0
    if (isQuotationDTO(this.quotation)) {
      totalLines = this.quotation.quotationLines.length || 0
    }
    return html`<span slot="badge">${totalLines}</span>`
  }

  get shouldOpenAsSlideIn(): boolean {
    if (this.windowLocation.pathname.includes('/account/')) {
      return false
    }

    return isQuotationDTO(this.quotation) && this.quotation.status === 'open'
  }

  async goToQuotationPage() {
    if (isQuotationDTO(this.quotation) && this.quotation.status === 'open') {
      await redirectToPath(this.windowLocation, `account/quotation/${this.quotation.id}/`)
      return
    }

    await redirectToPath(this.windowLocation, 'account/quotation/')
  }

  private async openSlideIn() {
    const slideIn: CSCQuotationRequestSlideIn = await new CSCQuotationRequestSlideIn().createSlideIn() as CSCQuotationRequestSlideIn
    slideIn.open()
  }

  async determineSlideInOrPage() {
    if (this.shouldOpenAsSlideIn) {
      await this.openSlideIn()
      return
    }

    await this.goToQuotationPage()
  }

  override async connectedCallback() {
    super.connectedCallback()

    listenForQuotationUpdates(async (quotation) => {
      if (!isQuotationDTO(quotation)) return
      await this.updateQuotationInternalWithoutRequest(quotation)
    })
  }

  override async updated() {
    if (!isQuotationDTO(this.quotation)) {
      await this.getOpenQuotation?.()
    }
  }

  override render() {
    const captionText = isQuotationDTO(this.quotation) && this.quotation.status === 'open'
      ? translations.whenOpenQuotation()
      : translations.whenNoOpenQuotation()

    if (this.asButton) {
      const totalLines = isQuotationDTO(this.quotation) ? this.quotation.quotationLines.length || 0 : 0
      return html`
        <span class="as-button-wrapper">
          <cse-button variant="primary" size=${this.size} @click=${this.determineSlideInOrPage}>
            ${!this.hideIcon
              ? html`<cse-icon slot="prefix" icon-role="csc-quotation-header-button.quotation-icon" size="${this.iconSize}"></cse-icon>`
              : nothing
            }
            ${!this.hideCaption ? captionText : nothing}
          </cse-button>
          <cse-badge variant="default">${totalLines}</cse-badge>
        </span>
      `
    }

    return html`
      <cse-header-button badge-variant="default" @click=${this.determineSlideInOrPage}>
        ${!this.hideIcon
          ? html`<cse-icon slot="icon" icon-role="csc-quotation-header-button.quotation-icon" size="${this.iconSize}"></cse-icon>`
          : nothing
        }
        ${this.totalProductsBadge}
        ${!this.hideCaption
          ? html`<span slot="caption">${captionText}</span>`
          : nothing
        }
      </cse-header-button>
    `
  }
}
